/**
 * A scheduled meeting (TRUS-691): the card in its conversation (a message with
 * metadata.meeting), in the upcoming list's details and behind a ?meeting=ID
 * link.
 *
 * Shows the title, the time in the viewer's local time (plus the org's time
 * zone when it differs), the host, attendees + RSVPs and the state:
 * upcoming → joinable (from joinable_from) → live → ended, or cancelled.
 * Actions: Join (from joinable_from), Add to calendar (ICS download / Google /
 * Outlook), Copy link, RSVP (attendees), and for the host Edit / Cancel.
 */

import React, { Suspense, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CalendarClock,
  CalendarPlus,
  Check,
  ChevronDown,
  Copy,
  Download,
  Loader2,
  Pencil,
  Phone,
  Users,
  Video as VideoIcon,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import {
  meetingPhase,
  myRsvpOf,
  type Meeting,
  type MeetingPhase,
  type MeetingRsvp,
} from '@/services/meetingsApi';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { formatMeetingRange, minutesUntil, orgTimeNote } from './meetingTime';
import { itemZoneTime, zonesDiffer } from '@/utils/zonedTime';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { copyToClipboard, saveBlob, useJoinMeeting, useMeetingCache, useMeetingsApi, useNow } from './useMeetings';
import { LazyScheduleMeetingDialog } from './lazyDialog';

interface MeetingCardProps {
  meeting: Meeting;
  /** The signed-in staff user (matches their attendee row for RSVP). */
  currentUserId?: number | null;
  /** The signed-in member (portal), when known. */
  currentMemberId?: number | null;
  /** Blue (own staff) bubble: light text. */
  tone?: 'default' | 'inverted';
  /** Rendered under the actions (phase 2: guest link controls). */
  footer?: React.ReactNode;
}

const RSVP_CHOICES: MeetingRsvp[] = ['accepted', 'tentative', 'declined'];

export function MeetingCard({ meeting, currentUserId, currentMemberId, tone = 'default', footer }: MeetingCardProps) {
  const { t, language: locale } = useAppTranslation();
  const tz = useUserTimezone(meeting.org_timezone);
  const api = useMeetingsApi();
  const now = useNow(15_000);
  const phase = meetingPhase(meeting, now);
  const { join, pendingId } = useJoinMeeting();
  const update = useMeetingCache();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const inverted = tone === 'inverted';
  const muted = inverted ? 'text-white/80' : 'text-muted-foreground';

  const isHost = meeting.i_am_host || (currentUserId != null && meeting.host.user_id === currentUserId);
  const myRsvp = myRsvpOf(meeting, { userId: currentUserId, memberId: currentMemberId });
  // Attendees answer; the host does not RSVP to their own meeting.
  const canRsvp = !isHost && phase !== 'cancelled' && phase !== 'ended' && (myRsvp != null || api.kind === 'member');

  const rsvp = useMutation({
    mutationFn: (value: MeetingRsvp) => api.rsvp(meeting.id, value),
    onSuccess: (fresh, value) => update(fresh ?? { ...meeting, my_rsvp: value }),
    onError: (err: unknown) =>
      toast.error(t('messaging.meetings.rsvpFailed', 'Could not save your answer'), { description: (err as Error)?.message }),
  });
  const cancel = useMutation({
    mutationFn: () => api.cancel(meeting.id),
    onSuccess: (fresh) => {
      setConfirmCancel(false);
      update(fresh);
      toast.success(t('messaging.meetings.cancelled', 'Meeting cancelled. Attendees are told.'));
    },
    onError: (err: unknown) =>
      toast.error(t('messaging.meetings.cancelFailed', 'Could not cancel the meeting'), { description: (err as Error)?.message }),
  });

  const title = meeting.title || t('messaging.meetings.untitled', 'Meeting');
  const when = formatMeetingRange(meeting.starts_at, meeting.ends_at, locale, tz);
  // TRUS-697: the meeting's own time when it was scheduled in another zone; the
  // org's time only when it says something the meeting's time does not.
  const meetingNote = itemZoneTime(meeting.starts_at, tz, meeting.timezone, locale);
  const orgNoteRaw = orgTimeNote(meeting.starts_at, meeting.org_timezone, locale, tz);
  const orgNote =
    orgNoteRaw && meetingNote && !zonesDiffer(meeting.starts_at, meeting.timezone || tz, meeting.org_timezone)
      ? null
      : orgNoteRaw;
  const link = api.kind === 'member' ? meeting.join_url_member : meeting.join_url_staff;

  const counts = useMemo(() => {
    const c = { accepted: 0, declined: 0, tentative: 0, pending: 0 };
    for (const a of meeting.attendees) c[a.rsvp] += 1;
    return c;
  }, [meeting.attendees]);

  const phaseLabel = (p: MeetingPhase): string => {
    switch (p) {
      case 'live':
        return t('messaging.meetings.phaseLive', 'Live now');
      case 'joinable':
        return t('messaging.meetings.phaseJoinable', 'Starting soon');
      case 'ended':
        return t('messaging.meetings.phaseEnded', 'Ended');
      case 'cancelled':
        return t('messaging.meetings.phaseCancelled', 'Cancelled');
      default: {
        const mins = minutesUntil(meeting.starts_at, now);
        return mins != null && mins <= 60
          ? t('messaging.meetings.startsInMinutes', 'Starts in {{count}} min', { count: mins })
          : t('messaging.meetings.phaseUpcoming', 'Upcoming');
      }
    }
  };
  const rsvpLabel = (r: MeetingRsvp) =>
    r === 'accepted'
      ? t('messaging.meetings.rsvpAccept', 'Accept')
      : r === 'declined'
        ? t('messaging.meetings.rsvpDecline', 'Decline')
        : r === 'tentative'
          ? t('messaging.meetings.rsvpMaybe', 'Maybe')
          : t('messaging.meetings.rsvpPending', 'No answer');

  const canJoin = phase === 'joinable' || phase === 'live';
  const joining = pendingId === meeting.id;

  const copyLink = async () => {
    if (!link) return;
    const ok = await copyToClipboard(link);
    if (ok) toast.success(t('messaging.meetings.linkCopied', 'Meeting link copied'));
    else toast.error(t('messaging.meetings.copyFailed', 'Could not copy the link'));
  };
  const downloadIcs = async () => {
    // The signed, no-auth link (contract): a plain download, works in the desktop app too.
    if (meeting.ics_url) {
      try {
        const a = document.createElement('a');
        a.href = meeting.ics_url;
        a.download = `meeting-${meeting.id}.ics`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      } catch {
        /* fall back to the authenticated download */
      }
    }
    try {
      const blob = await api.ics(meeting.id);
      saveBlob(blob, `meeting-${meeting.id}.ics`);
    } catch (err) {
      toast.error(t('messaging.meetings.icsFailed', 'Could not download the calendar file'), {
        description: (err as Error)?.message,
      });
    }
  };
  const openExternal = (url: string) => {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* popup blocked */
    }
  };

  const ModeIcon = meeting.mode === 'audio' ? Phone : VideoIcon;

  return (
    <div
      className="flex min-w-[16rem] max-w-md flex-col gap-2 py-1"
      data-testid={`meeting-card-${meeting.id}`}
      data-phase={phase}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
            phase === 'live'
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : inverted
                ? 'bg-white/20 text-white'
                : 'bg-primary/10 text-primary',
          )}
        >
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className={cn('flex items-center gap-1.5 text-sm font-semibold', inverted && 'text-white')}>
            <span className={cn('truncate', phase === 'cancelled' && 'line-through')} data-testid="meeting-card-title">
              {title}
            </span>
            <ModeIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" aria-label={meeting.mode === 'audio' ? t('messaging.calls.audioCall', 'Audio call') : t('messaging.calls.videoCall', 'Video call')} />
          </div>
          <div className={cn('text-xs', muted)} data-testid="meeting-card-time">
            {when}
            {meetingNote && (
              <span data-testid="meeting-card-zone-time">
                {' '}
                {t('time.itemZone.meeting', '({{time}} meeting time)', { time: meetingNote })}
              </span>
            )}
          </div>
          {orgNote && (
            <div className={cn('text-[11px]', muted)} data-testid="meeting-card-org-time">
              {t('messaging.meetings.orgTime', '{{time}} in the organization’s time zone', { time: orgNote })}
            </div>
          )}
          <div className={cn('mt-0.5 flex flex-wrap items-center gap-x-2 text-xs', muted)}>
            <span
              className={cn(
                'font-medium',
                phase === 'live' && 'text-emerald-600 dark:text-emerald-400',
                phase === 'cancelled' && 'text-red-600 dark:text-red-400',
              )}
              data-testid="meeting-card-phase"
            >
              {phaseLabel(phase)}
            </span>
            {meeting.host.name && (
              <span>{t('messaging.meetings.hostedBy', 'Host: {{name}}', { name: meeting.host.name })}</span>
            )}
            <span className="inline-flex items-center gap-1" data-testid="meeting-card-attendees">
              <Users className="h-3 w-3" aria-hidden="true" />
              {t('messaging.meetings.attendeeCount', '{{count}} invited', { count: meeting.attendees.length })}
              {counts.accepted > 0 && (
                <span>· {t('messaging.meetings.acceptedCount', '{{count}} going', { count: counts.accepted })}</span>
              )}
              {counts.declined > 0 && (
                <span>· {t('messaging.meetings.declinedCount', '{{count}} declined', { count: counts.declined })}</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {phase !== 'cancelled' && phase !== 'ended' && (
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="inline-flex"
            title={
              canJoin
                ? undefined
                : t('messaging.meetings.joinOpensHint', 'Join opens 10 minutes before the meeting starts.')
            }
          >
            <Button
              type="button"
              size="sm"
              className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
              disabled={!canJoin || joining}
              onClick={() => join(meeting)}
              data-testid={`meeting-join-${meeting.id}`}
            >
              {joining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ModeIcon className="h-3.5 w-3.5" />}
              {phase === 'live' ? t('messaging.meetings.joinNow', 'Join now') : t('messaging.meetings.join', 'Join')}
            </Button>
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1"
                data-testid={`meeting-calendar-${meeting.id}`}
              >
                <CalendarPlus className="h-3.5 w-3.5" />
                {t('messaging.meetings.addToCalendar', 'Add to calendar')}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={() => void downloadIcs()} data-testid="meeting-calendar-ics">
                <Download className="mr-2 h-4 w-4" />
                {t('messaging.meetings.downloadIcs', 'Download (.ics)')}
              </DropdownMenuItem>
              {meeting.add_to_calendar.google && (
                <DropdownMenuItem
                  onSelect={() => openExternal(meeting.add_to_calendar.google!)}
                  data-testid="meeting-calendar-google"
                >
                  {t('messaging.meetings.googleCalendar', 'Google Calendar')}
                </DropdownMenuItem>
              )}
              {meeting.add_to_calendar.outlook && (
                <DropdownMenuItem
                  onSelect={() => openExternal(meeting.add_to_calendar.outlook!)}
                  data-testid="meeting-calendar-outlook"
                >
                  {t('messaging.meetings.outlookCalendar', 'Outlook')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {link && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => void copyLink()}
              data-testid={`meeting-copy-link-${meeting.id}`}
            >
              <Copy className="h-3.5 w-3.5" />
              {t('messaging.meetings.copyLink', 'Copy link')}
            </Button>
          )}
          {meeting.can_edit && (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="gap-1"
                onClick={() => setEditOpen(true)}
                data-testid={`meeting-edit-${meeting.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('messaging.meetings.edit', 'Edit')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="gap-1 text-red-600 hover:text-red-700 dark:text-red-400"
                onClick={() => setConfirmCancel(true)}
                data-testid={`meeting-cancel-${meeting.id}`}
              >
                <XCircle className="h-3.5 w-3.5" />
                {t('messaging.meetings.cancelMeeting', 'Cancel meeting')}
              </Button>
            </>
          )}
        </div>
      )}

      {canRsvp && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          role="group"
          aria-label={t('messaging.meetings.rsvpLabel', 'Your answer')}
          data-testid={`meeting-rsvp-${meeting.id}`}
        >
          <span className={cn('text-xs', muted)}>{t('messaging.meetings.goingQuestion', 'Going?')}</span>
          {RSVP_CHOICES.map((choice) => {
            const selected = myRsvp === choice;
            return (
              <Button
                key={choice}
                type="button"
                size="sm"
                variant={selected ? 'default' : 'outline'}
                className="h-7 gap-1 px-2 text-xs"
                aria-pressed={selected}
                disabled={rsvp.isPending}
                onClick={() => rsvp.mutate(choice)}
                data-testid={`meeting-rsvp-${choice}`}
              >
                {selected && <Check className="h-3 w-3" />}
                {rsvpLabel(choice)}
              </Button>
            );
          })}
        </div>
      )}

      {footer}

      {editOpen && (
        <Suspense fallback={null}>
          <LazyScheduleMeetingDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            meeting={meeting}
            channelId={meeting.channel_id}
          />
        </Suspense>
      )}

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-md" data-testid="meeting-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{t('messaging.meetings.cancelTitle', 'Cancel this meeting?')}</DialogTitle>
            <DialogDescription>
              {t(
                'messaging.meetings.cancelBody',
                'Everyone invited gets a cancellation, and the meeting is removed from their calendars.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setConfirmCancel(false)}>
              {t('messaging.meetings.keep', 'Keep it')}
            </Button>
            <Button
              type="button"
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
              data-testid="meeting-cancel-confirm"
            >
              {cancel.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {t('messaging.meetings.cancelConfirm', 'Cancel meeting')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MeetingCard;
